import api from './api';

export type ContextType =
  | 'personal'
  | 'family'
  | 'business'
  | 'shared_living'
  | 'trip'
  | 'project'
  | 'friends'
  | 'shared';

export type ContextVisibility = 'private' | 'invite_only' | 'public';

export type MemberRole = 'owner' | 'admin' | 'member' | 'viewer';

export type MemberStatus = 'active' | 'invited' | 'suspended' | 'left';

export interface FinancialContext {
  id: string;
  name: string;
  description?: string;
  type: ContextType;
  visibility: ContextVisibility;
  defaultCurrency: string;
  timezone?: string;
  isActive: boolean;
  color?: string;
  icon?: string;
  /** Free-form context preferences; the monthly budget lives here. */
  settings?: Record<string, any> | null;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  /** The signed-in user's role in this context, attached by the list endpoint. */
  memberRole?: MemberRole;
  /**
   * The Telegram groups linked to this context, attached by the list
   * endpoint: `url` opens the chat; `needsAdmin` means a basic group that only
   * gets one once Fin is an admin there; `inviteLink` is what an invitation
   * can carry (a message link only opens for members).
   */
  telegramChats?: Array<{ title: string | null; url?: string | null; needsAdmin?: boolean; inviteLink?: string | null }>;
}

/** Why nobody can be made an admin of the group's Telegram group by Fin, at this person's request. */
export type TelegramAdminGroupReason =
  | 'no_group'
  | 'not_supergroup'
  | 'fin_cannot_promote'
  | 'you_cannot_promote'
  | 'telegram_unavailable';
/** Why this person can't be. */
export type TelegramAdminMemberReason =
  | 'no_telegram'
  | 'not_in_group'
  | 'owner_in_telegram'
  | 'admin_in_telegram'
  | 'telegram_unavailable';

export interface TelegramAdminState {
  memberId: string;
  /** An admin of the Telegram group made by Fin. */
  on: boolean;
  unavailable: TelegramAdminMemberReason | null;
}

export interface TelegramAdminsStatus {
  unavailable: TelegramAdminGroupReason | null;
  /** One per admin of the app group. */
  members: TelegramAdminState[];
}

export interface ContextMember {
  id: string;
  role: MemberRole;
  status: MemberStatus;
  joinedAt?: string;
  invitedAt?: string;
  inviteMessage?: string;
  inviteExpiresAt?: string;
  /** Only present on the invite response: whether the email actually went out. */
  invitationEmailSent?: boolean;
  contextId: string;
  userId: string;
  user?: {
    id: string;
    /** Only on a pending invitation, and only for owners and admins. */
    email?: string;
    firstName: string;
    lastName: string;
    /** A profile that came from Telegram, with no app account yet. */
    isGuest?: boolean;
  };
}

export interface CreateContextData {
  name: string;
  description?: string;
  type: ContextType;
  visibility?: ContextVisibility;
  defaultCurrency?: string;
  color?: string;
  icon?: string;
  settings?: Record<string, any>;
}

export type UpdateContextData = Partial<CreateContextData>;

export type { SplitRule, SplitSettings } from './splitRules';
export { SPLIT_RULES, PICKABLE_SPLIT_RULES, suggestedSplitRule, splitRuleOf, withSplitRule } from './splitRules';

export interface InviteMemberData {
  email: string;
  role: MemberRole;
  message?: string;
}

/**
 * What inviting answers: a membership waiting for an existing account, or,
 * for an email with no account, an invitation to sign up (`pendingSignup`).
 */
export interface InviteResult {
  inviteToken?: string;
  invitationEmailSent?: boolean;
  pendingSignup?: boolean;
  email?: string;
}

/** An invitation to sign up nobody has taken up yet, as Membros lists it. */
export interface SignupInvitation {
  id: string;
  email: string;
  role: MemberRole;
  expiresAt: string;
  createdAt: string;
}

/** What an invitation link is for, shown before signing in. */
export interface InvitationPreview {
  kind: 'signup' | 'member';
  contextName: string;
  inviterName: string | null;
  role: MemberRole;
  email: string | null;
  expired: boolean;
}

export interface MemberBalance {
  userId: string;
  firstName: string;
  lastName: string;
  /** What this member paid for the group. */
  paid: number;
  /** What this member's own parts add up to. */
  owed: number;
  /** paid − owed: positive is money to receive, negative is money to pay. */
  net: number;
  /** Invited and not accepted yet: counted all the same, shown with a note. */
  pending?: boolean;
}

/** `fromUserId` owes `amount` to `toUserId`, already netted per pair. */
export interface PairBalance {
  fromUserId: string;
  toUserId: string;
  amount: number;
}

export interface ContextBalances {
  contextId: string;
  contextName: string;
  currency: string;
  members: MemberBalance[];
  pairs: PairBalance[];
}

export interface SettlementData {
  /** Who paid; defaults to the caller. Set it to record a payment the caller received. */
  fromUserId?: string;
  toUserId: string;
  amount: number;
  date?: string;
  note?: string;
}

export const contextApi = {
  async list(): Promise<FinancialContext[]> {
    const response = await api.get('/contexts');
    return response.data;
  },

  async get(id: string): Promise<FinancialContext & { members?: ContextMember[] }> {
    const response = await api.get(`/contexts/${id}`);
    return response.data;
  },

  async create(data: CreateContextData): Promise<FinancialContext> {
    const response = await api.post('/contexts', data);
    return response.data;
  },

  async update(id: string, data: UpdateContextData): Promise<FinancialContext> {
    const response = await api.patch(`/contexts/${id}`, data);
    return response.data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/contexts/${id}`);
  },

  async getMembers(id: string): Promise<ContextMember[]> {
    const response = await api.get(`/contexts/${id}/members`);
    return response.data;
  },

  async invite(id: string, data: InviteMemberData): Promise<InviteResult> {
    const response = await api.post(`/contexts/${id}/invite`, data);
    return response.data;
  },

  async getSignupInvitations(id: string): Promise<SignupInvitation[]> {
    const response = await api.get(`/contexts/${id}/invitations`);
    return response.data;
  },

  async cancelSignupInvitation(id: string, invitationId: string): Promise<void> {
    await api.delete(`/contexts/${id}/invitations/${invitationId}`);
  },

  /** About to add Fin to a Telegram group for this group: the server links the next one. */
  async markTelegramIntent(id: string): Promise<void> {
    await api.post(`/contexts/${id}/telegram-intent`);
  },

  /** Needs no session: the link is opened before signing in. */
  async previewInvitation(token: string): Promise<InvitationPreview> {
    const response = await api.get(`/contexts/invitations/${token}`);
    return response.data;
  },

  async acceptInvitation(token: string): Promise<ContextMember> {
    const response = await api.post(`/contexts/invitations/${token}/accept`);
    return response.data;
  },

  async updateMemberRole(id: string, memberId: string, role: MemberRole): Promise<ContextMember> {
    const response = await api.patch(`/contexts/${id}/members/${memberId}/role`, { role });
    return response.data;
  },

  /** For owners and admins: whether each admin can be, or is, an admin of the Telegram group too. */
  async getTelegramAdmins(id: string): Promise<TelegramAdminsStatus> {
    const response = await api.get(`/contexts/${id}/telegram-admins`);
    return response.data;
  },

  /** Fin makes an admin of the group an admin of its Telegram group, or takes it back. */
  async setTelegramAdmin(id: string, memberId: string, enabled: boolean): Promise<TelegramAdminState> {
    const response = await api.put(`/contexts/${id}/members/${memberId}/telegram-admin`, { enabled });
    return response.data;
  },

  async removeMember(id: string, memberId: string): Promise<void> {
    await api.delete(`/contexts/${id}/members/${memberId}`);
  },

  async leave(id: string): Promise<void> {
    await api.post(`/contexts/${id}/leave`);
  },

  /** Who owes whom in one context. */
  async getBalances(id: string): Promise<ContextBalances> {
    const response = await api.get(`/contexts/${id}/balances`);
    return response.data;
  },

  /** Who owes whom in every shared context of the signed-in user. */
  async getAllBalances(): Promise<ContextBalances[]> {
    const response = await api.get('/contexts/balances');
    return response.data;
  },

  /** "I paid this member back": recorded as a confirmed transfer. */
  async settle(id: string, data: SettlementData): Promise<void> {
    await api.post(`/contexts/${id}/settlements`, data);
  },
};

/**
 * The invitation link: the one the email carries, and the one to share by
 * hand when no email went out.
 */
export const invitationUrl = (token: string): string =>
  `${window.location.origin}/invitations/${token}`;
